import React, { useEffect, useState } from "react";
import { Button, Dropdown, Modal, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import {
  dockerAction,
  dockerList,
  dockerLogs,
} from "../../redux/actionCreator";
import { Container, RotateCw, Square, Play, FileText } from "lucide-react";
import "./dockerPanel.scss";

/**
 * `docker ps -a --format '{{json .}}'` returns rows with at least:
 *   ID, Names, Image, Command, RunningFor, Status, State, Ports, CreatedAt
 *
 * `State` is the canonical machine-readable status string
 * (`running` | `exited` | `paused` | …). We use it for the badge color and
 * to decide which lifecycle buttons to enable.
 */
const STATE_BADGE = {
  running: "success",
  paused: "warning",
  restarting: "info",
  exited: "secondary",
  dead: "danger",
  created: "secondary",
  removing: "danger",
};

function shortId(id) {
  return id ? String(id).slice(0, 12) : "";
}

export default function DockerPanel({ show, onHide, project, server }) {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [logs, setLogs] = useState(null); // { cid, name, tail, log, loading }

  const projectId = project?._id;
  const serverId = server?._id;

  const load = async () => {
    if (!projectId || !serverId) return;
    setLoading(true);
    setErr(null);
    const res = await dispatch(dockerList(projectId, serverId));
    setLoading(false);
    if (res?.status) setRows(res.data || []);
    else {
      setRows([]);
      setErr(res?.message || "Failed to list containers");
    }
  };

  useEffect(() => {
    if (show) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, projectId, serverId]);

  const act = (cid, action) => {
    dispatch(
      dockerAction(projectId, serverId, cid, action, (ok) => ok && load())
    );
  };

  const openLogs = async (cid, name) => {
    setLogs({ cid, name, tail: 500, log: "", loading: true });
    const res = await dispatch(dockerLogs(projectId, serverId, cid, 500));
    if (res?.status) {
      setLogs({ cid, name, tail: 500, log: res.data?.log || "", loading: false });
    } else {
      setLogs({ cid, name, tail: 500, log: res?.message || "(no logs)", loading: false });
    }
  };

  const openTerminal = (cid) => {
    const qs = new URLSearchParams({
      label: `${server.label} · ${cid.slice(0, 12)}`,
      host: `${server.username}@${server.host}:${server.port}`,
      container: cid,
    });
    window.open(
      `/terminal/${projectId}/${serverId}?${qs.toString()}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered>
        <Modal.Header closeButton>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <h4 className="m-0">Docker</h4>
            <span className="text-muted">{server?.label}</span>
            <code className="small">
              {server?.username}@{server?.host}:{server?.port}
            </code>
          </div>
        </Modal.Header>
        <Modal.Body className="docker-panel-body">
          <div className="docker-toolbar">
            <Button size="sm" variant="light" onClick={load}>
              Refresh
            </Button>
            <span className="text-muted ms-auto small">
              {rows.length} container{rows.length === 1 ? "" : "s"}
            </span>
          </div>

          {err && <div className="alert alert-danger m-3">{err}</div>}

          <div className="docker-table-wrap">
            <table className="docker-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Image</th>
                  <th>State</th>
                  <th>Status</th>
                  <th>Ports</th>
                  <th className="text-end" style={{ width: 1 }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="empty">
                      <Spinner size="sm" animation="border" /> Loading…
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="empty">
                      <div className="empty-state">
                        <div className="empty-art"><Container size={32} strokeWidth={1.2} /></div>
                        <h4>No containers</h4>
                        <p>
                          Either docker isn't installed on this host, or the
                          SSH user lacks permission to run <code>docker</code>.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  rows.map((c) => {
                    const cid = c.ID || c.Id || c.ContainerID;
                    const name = c.Names || c.Name || shortId(cid);
                    const state = (c.State || "").toLowerCase();
                    const isRunning = state === "running";
                    return (
                      <tr key={cid}>
                        <td>
                          <div className="emp-name">{name}</div>
                          <div className="emp-id">
                            <code>{shortId(cid)}</code>
                          </div>
                        </td>
                        <td>
                          <code className="small">{c.Image}</code>
                        </td>
                        <td>
                          <span
                            className={`badge bg-${
                              STATE_BADGE[state] || "secondary"
                            }`}
                          >
                            {state || "—"}
                          </span>
                        </td>
                        <td className="small">{c.Status}</td>
                        <td className="small">
                          <code>{c.Ports || ""}</code>
                        </td>
                        <td className="text-end">
                          {isRunning ? (
                            <>
                              <Button
                                size="sm"
                                variant="outline-warning"
                                className="me-1"
                                aria-label="Restart"
                                onClick={() => act(cid, "restart")}
                              >
                                <RotateCw size={12} />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                className="me-1"
                                onClick={() => act(cid, "stop")}
                              >
                                <Square size={12} fill="currentColor" className="me-1" /> Stop
                              </Button>
                            </>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline-success"
                              className="me-1"
                              onClick={() => act(cid, "start")}
                            >
                              <Play size={12} className="me-1" /> Start
                            </Button>
                          )}
                          <Dropdown align="end" className="d-inline">
                            <Dropdown.Toggle
                              size="sm"
                              variant="light"
                              className="row-actions-btn"
                            >
                              ⋯
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => openLogs(cid, name)}>
                                <FileText size={12} className="me-1" /> Logs
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() => openTerminal(cid)}
                                disabled={!isRunning}
                                title={
                                  isRunning
                                    ? "Open shell inside the container"
                                    : "Container must be running"
                                }
                              >
                                <Play size={12} className="me-1" /> Terminal (inside)
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {logs && (
        <Modal show onHide={() => setLogs(null)} size="xl" centered>
          <Modal.Header closeButton>
            <h5 className="m-0">
              Logs — <code>{logs.name}</code>{" "}
              <small className="text-muted">last {logs.tail} lines</small>
            </h5>
          </Modal.Header>
          <Modal.Body className="p-0">
            {logs.loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
              </div>
            ) : (
              <pre className="docker-logs">{logs.log || "(empty)"}</pre>
            )}
          </Modal.Body>
        </Modal>
      )}
    </>
  );
}
